import type { SlideElement, SlideTemplate } from "@ai-teach/types";
import { isFreeformTemplate } from "@ai-teach/types";
import { CANVAS_SIZE } from "../constants/slides";

/**
 * Canvas size configuration
 */
export interface CanvasSize {
  height: number;
  aspectRatio: number;
}

/**
 * Get canvas width from height and aspect ratio
 */
export function getCanvasWidth(canvasSize: CanvasSize): number {
  return canvasSize.height * canvasSize.aspectRatio;
}

/**
 * Convert element position and size from pixels to percentages
 * This is used when saving elements to the database
 */
export function convertElementToPercentages(
  element: SlideElement,
  template: SlideTemplate | null
): SlideElement {
  const canvasSize = isFreeformTemplate(template)
    ? CANVAS_SIZE.freeform.size
    : CANVAS_SIZE.editor.size;
  const { height: canvasHeight, width: canvasWidth } = canvasSize;

  // Convert position to percentages
  const position = {
    x: (element.position.x / canvasWidth) * 100,
    y: (element.position.y / canvasHeight) * 100,
  };

  // Convert size to percentages
  const size = {
    width:
      typeof element.size.width === "number"
        ? (element.size.width / canvasWidth) * 100
        : element.size.width,
    height:
      typeof element.size.height === "number"
        ? (element.size.height / canvasHeight) * 100
        : element.size.height,
  };

  if (element.type === "line") {
    const startX = (element.startX / canvasWidth) * 100;
    const startY = (element.startY / canvasHeight) * 100;
    const endX = (element.endX / canvasWidth) * 100;
    const endY = (element.endY / canvasHeight) * 100;

    return {
      ...element,
      startX,
      startY,
      endX,
      endY,
    };
  }

  return {
    ...element,
    position,
    size,
  };
}

/**
 * Convert element position and size from percentages to pixels
 * This is used when loading elements from the database
 */
export function convertElementToPixels(
  element: SlideElement,
  template: SlideTemplate | null
): SlideElement {
  const canvasSize = isFreeformTemplate(template)
    ? CANVAS_SIZE.freeform.size
    : CANVAS_SIZE.editor.size;
  const { height: canvasHeight, width: canvasWidth } = canvasSize;

  // Convert position to pixels
  const position = {
    x: (Number(element.position.x) / 100) * canvasWidth,
    y: (Number(element.position.y) / 100) * canvasHeight,
  };

  // Convert size to pixels
  const size = {
    width:
      typeof element.size.width === "number"
        ? (element.size.width / 100) * canvasWidth
        : element.size.width,
    height:
      typeof element.size.height === "number"
        ? (element.size.height / 100) * canvasHeight
        : element.size.height,
  };

  if (element.type === "line") {
    const startX =
      typeof element.startX === "number" ? (element.startX / 100) * canvasWidth : element.startX;

    const startY =
      typeof element.startY === "number" ? (element.startY / 100) * canvasHeight : element.startY;

    const endX =
      typeof element.endX === "number" ? (element.endX / 100) * canvasWidth : element.endX;

    const endY =
      typeof element.endY === "number" ? (element.endY / 100) * canvasHeight : element.endY;

    return {
      ...element,
      startX,
      startY,
      endX,
      endY,
    };
  }

  return {
    ...element,
    position,
    size,
  };
}

/**
 * Convert multiple elements to percentages
 */
export function convertElementsToPercentages(
  elements: SlideElement[],
  template: SlideTemplate | null
): SlideElement[] {
  return elements.map((element) => convertElementToPercentages(element, template));
}

/**
 * Convert multiple elements to pixels
 */
export function convertElementsToPixels(
  elements: SlideElement[],
  template: SlideTemplate | null
): SlideElement[] {
  return elements.map((element) => convertElementToPixels(element, template));
}
